import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Drawer,
  Button,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import notify from '../../utils/notifier';
import DocuwareTemplateService from '../../services/DocuwareTemplateService';

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: '45%',
    padding: 28,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 16,
    borderBottom: '1px solid #E0E0E0'
  },
  title: { fontWeight: 700, fontSize: 18 },
  subtitle: { 
    fontSize: 13, 
    color: '#6B7280',
    marginTop: 4
  },
  content: { 
    flex: 1, 
    overflowY: 'auto', 
    marginTop: 16 
  },
  uploadSection: {
    marginBottom: 24
  },
  dropZone: {
    border: '2px dashed #C3DDEE',
    borderRadius: 8,
    padding: 32,
    textAlign: 'center',
    backgroundColor: '#F8FBFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#00445D',
      backgroundColor: '#E9F4FA'
    }
  },
  dropZoneActive: {
    borderColor: '#00445D',
    backgroundColor: '#E9F4FA'
  },
  dropZoneIcon: {
    fontSize: 48,
    color: '#00445D',
    marginBottom: 12
  },
  dropZoneText: {
    fontSize: 14,
    color: '#1E293B',
    marginBottom: 4
  },
  dropZoneSubtext: {
    fontSize: 12,
    color: '#6B7280'
  },
  uploadButton: {
    backgroundColor: '#00445D',
    color: '#fff',
    textTransform: 'none',
    borderRadius: 24,
    padding: '8px 20px',
    marginTop: 12,
    '&:hover': { backgroundColor: '#0d1b5e' }
  },
  samplesList: {
    marginTop: 16
  },
  samplesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12
  },
  samplesTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1E293B'
  },
  samplesCount: {
    fontSize: 13,
    color: '#6B7280'
  },
  sampleItem: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginBottom: 8,
    padding: '12px 16px',
    border: '1px solid #E5E7EB',
    '&:hover': {
      backgroundColor: '#F3F4F6'
    }
  },
  sampleIcon: {
    color: '#00445D',
    marginRight: 12
  },
  sampleName: {
    fontWeight: 500,
    fontSize: 14,
    color: '#1E293B'
  },
  sampleMeta: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2
  },
  samplePreview: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '90%'
  },
  deleteButton: {
    color: '#9CA3AF',
    '&:hover': {
      color: '#D32F2F'
    }
  },
  emptyState: {
    textAlign: 'center',
    padding: 32,
    color: '#9CA3AF'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32
  },
  uploadProgress: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#E9F4FA',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  hiddenInput: {
    display: 'none'
  }
}));

export default function CategorySamplesDrawer({ open, onClose, category }) {
  const classes = useStyles();
  const [samples, setSamples] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [dragActive, setDragActive] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const fetchSamples = React.useCallback(async () => {
    if (!category?.id) return;
    
    setLoading(true);
    try {
      const resp = await DocuwareTemplateService.getCategorySamples(category.id);
      const data = resp?.data;
      
      if (data?.samples && Array.isArray(data.samples)) {
        setSamples(data.samples);
      } else if (Array.isArray(data)) {
        setSamples(data);
      } else {
        setSamples([]);
      }
    } catch (err) {
      console.error('Failed to fetch samples', err);
      notify({ type: 'error', message: 'Failed to load samples' });
    } finally {
      setLoading(false);
    }
  }, [category?.id]);

  React.useEffect(() => {
    if (open && category?.id) {
      fetchSamples();
    } else {
      setSamples([]);
    }
  }, [open, category?.id, fetchSamples]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0 || !category?.id) return;

    setUploading(true);
    try {
      const resp = await DocuwareTemplateService.uploadCategorySample(category.id, Array.from(files));
      
      const data = resp?.data;
      if (data?.failed && data.failed.length > 0) {
        const failedNames = data.failed.map(f => f.filename).join(', ');
        notify({ type: 'warning', message: `Some files failed to upload: ${failedNames}` });
      } else {
        notify({ type: 'success', message: 'Sample(s) uploaded successfully' });
      }
      
      await fetchSamples();
    } catch (err) {
      console.error('Error uploading samples', err);
      const errorMsg = err?.response?.data?.message || 'Failed to upload samples';
      notify({ type: 'error', message: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSample = async (sampleId, filename) => {
    try {
      await DocuwareTemplateService.deleteCategorySample(sampleId);
      notify({ type: 'success', message: `Sample "${filename}" deleted` });
      await fetchSamples();
    } catch (err) {
      console.error('Error deleting sample', err);
      notify({ type: 'error', message: 'Failed to delete sample' });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
    }
    e.target.value = '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTextLength = (length) => {
    if (!length) return '0 chars';
    if (length >= 1000) {
      return `${(length / 1000).toFixed(1)}k chars`;
    }
    return `${length} chars`;
  };

  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>
            {category?.name || 'Category'} - Samples
          </Typography>
          <Typography className={classes.subtitle}>
            Upload sample documents to train the classification system
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.content}>
        {/* Upload Section */}
        <Box className={classes.uploadSection}>
          <input
            type="file"
            ref={fileInputRef}
            className={classes.hiddenInput}
            onChange={handleFileInputChange}
            multiple
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
          />
          
          <Box
            className={`${classes.dropZone} ${dragActive ? classes.dropZoneActive : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadIcon className={classes.dropZoneIcon} />
            <Typography className={classes.dropZoneText}>
              Drop files here or click to browse
            </Typography>
            <Typography className={classes.dropZoneSubtext}>
              Supported: PDF, DOC, DOCX, TXT, PNG, JPG
            </Typography>
          </Box>

          {uploading && (
            <Box className={classes.uploadProgress}>
              <CircularProgress size={20} />
              <Typography style={{ fontSize: 14 }}>Uploading samples...</Typography>
            </Box>
          )}
        </Box>

        <Divider style={{ marginBottom: 16 }} />

        {/* Samples List */}
        <Box className={classes.samplesList}>
          <Box className={classes.samplesHeader}>
            <Typography className={classes.samplesTitle}>Uploaded Samples</Typography>
            <Typography className={classes.samplesCount}>
              {samples.length} sample{samples.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={32} />
            </Box>
          ) : samples.length === 0 ? (
            <Box className={classes.emptyState}>
              <InsertDriveFileIcon style={{ fontSize: 48, marginBottom: 8 }} />
              <Typography>No samples uploaded yet</Typography>
              <Typography style={{ fontSize: 12, marginTop: 4 }}>
                Upload sample documents to improve classification accuracy
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {samples.map((sample) => (
                <ListItem key={sample.id} className={classes.sampleItem} disableGutters>
                  <InsertDriveFileIcon className={classes.sampleIcon} />
                  <ListItemText
                    primary={
                      <Typography className={classes.sampleName}>
                        {sample.originalFilename || sample.original_filename || 'Unknown file'}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography className={classes.sampleMeta}>
                          {formatTextLength(sample.textLength || sample.text_length)} | {formatDate(sample.createdAt || sample.created_at)}
                        </Typography>
                        {(sample.textPreview || sample.text_preview) && (
                          <Typography className={classes.samplePreview}>
                            "{sample.textPreview || sample.text_preview}"
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      className={classes.deleteButton}
                      onClick={() => handleDeleteSample(sample.id, sample.originalFilename || sample.original_filename)}
                      title="Delete sample"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
